import { memo, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import Svg from 'react-native-svg';
import { CategoryTooltip } from './CategoryTooltip';
import { ComboAxes } from './ComboAxes';
import { ComboBars } from './ComboBars';
import { ComboLines } from './ComboLines';
import { comboLayout } from './layout';
import {
  ComboBarStyle,
  ComboCategoryAxis,
  ComboLineStyle,
  ComboSeries,
  ComboTheme,
  ComboValueAxis,
} from './types';

export interface ComboChartProps {
  width: number;
  height: number;
  categories: string[];
  /** Each drawn as its own type against its own axis. */
  series: ComboSeries[];
  /** Left-axis bars stack into one column; lines and areas stay out. */
  stacked?: boolean;
  xAxis: ComboCategoryAxis;
  yAxis: ComboValueAxis;
  /** The right axis: drawn when some series uses it. */
  y2Axis?: ComboValueAxis;
  gridLines: boolean;
  dataLabels: boolean;
  /** A stacked label too wide for its bar, short. Omitted: the y ticks'. */
  compactLabel?: (value: number) => string;
  line: ComboLineStyle;
  bar: ComboBarStyle;
  theme: ComboTheme;
}

/**
 * A category chart whose series each draw as bars, a line or an area on a
 * left or a right value axis: plain line, area, bar and stacked bar charts
 * are the case where every series shares one type. Hovering or tapping a
 * category shows its full name and values.
 */
export const ComboChart = memo((props: ComboChartProps) => {
  const { width, height, series, theme } = props;
  const stacked = !!props.stacked;
  const layout = useMemo(
    () =>
      comboLayout({
        width,
        height,
        categories: props.categories,
        series,
        stacked,
        xAxis: props.xAxis,
        yAxis: props.yAxis,
        y2Axis: props.y2Axis,
        dataLabels: props.dataLabels,
        fontSize: theme.fontSize,
      }),
    [
      width,
      height,
      props.categories,
      series,
      stacked,
      props.xAxis,
      props.yAxis,
      props.y2Axis,
      props.dataLabels,
      theme.fontSize,
    ],
  );
  const bands = useMemo(
    () =>
      props.categories.map((_, i) => ({
        x: layout.plot.left + layout.band * i,
        y: layout.plot.top,
        width: layout.band,
        height: height - layout.plot.top,
      })),
    [props.categories, layout, height],
  );
  const { yAxis, y2Axis } = props;
  const rowsOf = useCallback(
    (i: number) =>
      series.flatMap(entry => {
        const value = entry.values[i];
        if (value === undefined) return [];
        const axis = entry.axis === 'right' && y2Axis ? y2Axis : yAxis;
        return [
          {
            color: entry.color,
            name: entry.name,
            value: entry.labels?.[i] ?? axis.format(value),
          },
        ];
      }),
    [series, yAxis, y2Axis],
  );
  return (
    <View style={{ width, height }}>
      <Svg width={width} height={height}>
        <ComboAxes
          layout={layout}
          width={width}
          height={height}
          xAxis={props.xAxis}
          yAxis={props.yAxis}
          y2Axis={props.y2Axis}
          gridLines={props.gridLines}
          theme={theme}
        />
        <ComboBars
          layout={layout}
          series={series}
          stacked={stacked}
          bar={props.bar}
          dataLabels={props.dataLabels}
          compact={props.compactLabel ?? props.yAxis.format}
          theme={theme}
        />
        <ComboLines
          layout={layout}
          series={series}
          line={props.line}
          dataLabels={props.dataLabels}
          theme={theme}
        />
      </Svg>
      <CategoryTooltip
        width={width}
        height={height}
        bands={bands}
        names={props.categories}
        rowsOf={rowsOf}
        theme={theme}
      />
    </View>
  );
});
